import { Box, DialogTitle, Grid, Typography } from "@mui/material";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import useIsLoading from "../../../hooks/useIsLoading";
import {
  EmploymentsHierarchy,
  EmploymentsHierarchyResponse,
  Talent,
  CancelledEmployment,
  talentIsDeactivated,
} from "../../talents/talentAPI";
import { fetchEmploymentsHierarchy } from "../../talents/talentsSlice";
import { AccessRightContext } from "../../user/AccessRightProvider";
import BundleSection from "./BundleSection/BundleSection";
import { CancelledEmploymentsSection } from "./CancelledEmploymentsSection/CancelledEmploymentsSection";
import PaymentProhibition from "./PaymentProhibition/PaymentProhibition";
import PayslipOverview from "./PayslipOverview/PayslipOverview";
import UnionFee from "./UnionFee/UnionFee";
import Upcoming from "./Upcoming/Upcoming";
import WitholdingTaxCorrection from "./WitholdingTaxCorrection/WitholdingTaxCorrection";
import { EmploymentRowType } from "./types";
import { CalculateExpensesTool } from "./CalculateExpensesTool";
import { isFulfilled } from "@reduxjs/toolkit";

interface Props {
  talent: Talent;
}

const Employment = ({ talent }: Props) => {
  // fetchEmploymentsHierarchy is updating talents status
  const isLoading = useIsLoading([
    "payslips",
    "paymentProhibitions",
    "talents",
  ]);

  const [employmentsHierarchy, setEmploymentsHierarchy] =
    useState<EmploymentsHierarchyResponse>({
      cancelledEmployments: [],
      hierarchy: [],
    });

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const { isAdmin, isSalaryBillingHr, canReadUnionPayment } =
    useContext(AccessRightContext);

  const canCalculateExpenses = isAdmin || isSalaryBillingHr;
  const isReadOnly = talentIsDeactivated(talent);

  const translatedTESNames = useAppSelector((state) => state.localized.tes);

  const refreshEmploymentsHierarchy = useCallback(() => {
    if (talent?.id) {
      dispatch(fetchEmploymentsHierarchy(talent.id)).then((action) => {
        if (isFulfilled(action)) {
          const responseData = action.payload;
          setEmploymentsHierarchy(responseData);
        }
      });
    }
  }, [talent.id, dispatch]);

  useEffect(() => {
    refreshEmploymentsHierarchy();
  }, [refreshEmploymentsHierarchy]);

  // Categorize employments based on their date range relative to the current date
  const categorizeEmploymentsByDate = (
    allData: EmploymentsHierarchyResponse,
  ) => {
    const data = allData.hierarchy;
    const currentDate = new Date();
    const upcomingEmployments: EmploymentsHierarchy[] = [];
    const openEmployments: EmploymentsHierarchy[] = [];
    const pastEmployments: EmploymentsHierarchy[] = [];
    const cancelledEmployments: CancelledEmployment[] =
      allData.cancelledEmployments;

    data.forEach((item: EmploymentsHierarchy) => {
      const startDate = new Date(item.startDate);
      const endDate = item.endDate ? new Date(item.endDate) : null;

      if (endDate && endDate < currentDate) {
        // If the endDate is in the past
        pastEmployments.push(item);
      } else if (startDate > currentDate) {
        // If the startDate is in the future
        upcomingEmployments.push(item);
      } else {
        // Otherwise, it's currently open
        openEmployments.push(item);
      }
    });

    return {
      upcomingEmployments,
      openEmployments,
      pastEmployments,
      cancelledEmployments,
    };
  };

  const {
    upcomingEmployments,
    openEmployments,
    pastEmployments,
    cancelledEmployments,
  } = useMemo(
    () => categorizeEmploymentsByDate(employmentsHierarchy),
    [employmentsHierarchy],
  );

  return (
    <Box
      sx={{
        background: "#F5F5F5",
        flexGrow: "1",
        paddingBottom: "16px",
        maxWidth: "1500px",
      }}
    >
      <OverlaySpinner isLoading={isLoading} />
      {canCalculateExpenses && !isReadOnly ? (
        <CalculateExpensesTool talentId={talent.id} />
      ) : null}
      <Box
        sx={{
          background: "#FFFFFF",
          padding: "0px 16px 16px 16px",
          borderRadius: "12px",
        }}
      >
        <DialogTitle
          sx={{
            paddingLeft: "16px",
            background: "#FFFFFF",
            borderRadius: "8px",
            paddingBottom: "16px",
          }}
        >
          <Typography
            variant="header"
            sx={{
              fontWeight: 600,
            }}
          >
            {t("talentProfile:employmentAndAttachment").toUpperCase()}
          </Typography>
        </DialogTitle>
        <Upcoming
          employmentsHierarchy={upcomingEmployments}
          translatedTESNames={translatedTESNames}
          type={EmploymentRowType.Upcoming}
        />
        <BundleSection
          sectionName={t("talentProfile:openEmployments")}
          employmentsHierarchy={openEmployments}
          translatedTESNames={translatedTESNames}
          bundleOpen={true}
          type={EmploymentRowType.Open}
          onRangeUpdated={refreshEmploymentsHierarchy}
        />
        <BundleSection
          sectionName={t("talentProfile:pastEmployments")}
          employmentsHierarchy={pastEmployments}
          translatedTESNames={translatedTESNames}
          type={EmploymentRowType.Past}
          onRangeUpdated={refreshEmploymentsHierarchy}
        />
        <CancelledEmploymentsSection
          sectionName={t("talentProfile:cancelledEmployments")}
          cancelledEmployments={cancelledEmployments}
        />
      </Box>
      {canReadUnionPayment ? (
        <>
          <Grid container spacing="16px" sx={{ marginBottom: "16px" }}>
            <Grid item xs={6}>
              <WitholdingTaxCorrection
                talentId={talent.id}
                isReadOnly={isReadOnly}
              />
            </Grid>
            <Grid item xs={6}>
              <UnionFee talentId={talent.id} isReadOnly={isReadOnly} />
            </Grid>
          </Grid>
          <Box marginBottom="16px">
            <PaymentProhibition talentId={talent.id} isReadOnly={isReadOnly} />
          </Box>
        </>
      ) : (
        <Box marginBottom="16px">
          <WitholdingTaxCorrection
            talentId={talent.id}
            isReadOnly={isReadOnly}
          />
        </Box>
      )}
      <Box>
        <PayslipOverview talentId={talent.id} isReadOnly={isReadOnly} />
      </Box>
    </Box>
  );
};

export default Employment;
